import { createServerFn } from "@tanstack/react-start";
import lodash from "lodash";

import { authMiddleware, mondayBoards } from "@onesolution/middlewares";

import {
	OPERATION_COLUMNS,
	PRICES_COLUMNS,
	USER_ROLES,
} from "@onesolution/constants";
import { rolesCheck } from "@onesolution/middlewares/rolesCheck";
import type { MONDAY_TYPES } from "@onesolution/monday";
import {
	formatOperationCreateColumnsForMutation,
	formatOperationItemsForMutation,
	formatOperationUpdateColumnsForMutation,
} from "@onesolution/monday/format";
import { mondayClient } from "@onesolution/monday/monday";
import {
	type OperationMutationVariables,
	OperationValidator,
} from "@onesolution/utils/operationValidator";
import { wrapWithUseMutation } from "..";
import { getCurrentUserDetails } from "../user";

export type PriceForOpCreation = MONDAY_TYPES["base"] & {
	channel: MONDAY_TYPES["status"];
	jobs: MONDAY_TYPES["dropdown"];
	prices: MONDAY_TYPES["text"];
	workflowSteps: MONDAY_TYPES["dropdown"];
};

export async function createOperationSubItems(
	mainOpId: string,
	subItems: OperationMutationVariables["subItems"],
) {
	const originalPricesIds = subItems?.map((si) => si.id);
	if (originalPricesIds?.length) {
		const originalsPrices = await mondayClient.query.items<PriceForOpCreation>({
			columnIds: [
				PRICES_COLUMNS.channel,
				PRICES_COLUMNS.jobs,
				PRICES_COLUMNS.prices,
				PRICES_COLUMNS.workflowSteps,
			],
			itemIds: originalPricesIds,
		});

		if (subItems?.length) {
			const allSubItems = subItems.flatMap(({ quantity, ...item }) =>
				[...Array(quantity).keys()].map(() => item),
			);
			const chunks = lodash.chunk(allSubItems, 10); // because monday limit concurent update
			for (const chunk of chunks) {
				await Promise.all(
					chunk.flatMap(({ id, deliveryDate, isNew }) =>
						mondayClient.mutation.createSubitem(
							formatOperationItemsForMutation(
								mainOpId,
								originalsPrices.find((original) => original.id === id)!,
								deliveryDate,
								isNew,
							),
						),
					),
				);
			}
		}
	}
}

const handler = createServerFn({ method: "POST" })
	.middleware([
		authMiddleware,
		mondayBoards,
		rolesCheck([USER_ROLES.Admin, USER_ROLES.Member]),
	])
	.validator(OperationValidator)
	.handler(async ({ context, data: { data, files, thumbnail } }) => {
		const user = await getCurrentUserDetails();
		if (lodash.isNil(context.boards)) throw new Error("Boards are empty");

		if (lodash.isNil(context.boards)) throw new Error("Boards are empty");

		//First create an op with empty values, THEN update it with right values
		//Because monday automation work only on item update, not creation (campaigns, services, marketing plans)
		const columnValuesCreate = formatOperationCreateColumnsForMutation(
			data,
			user.id,
		);

		const createdOperation = await mondayClient.mutation.createItem({
			boardId: context.boards.briefs,
			itemName: data.name,
			columnValues: JSON.stringify(columnValuesCreate),
		});

		if (!createdOperation) {
			throw { error: "Error while creating operation." };
		}

		const columnValues = await formatOperationUpdateColumnsForMutation(data);
		await mondayClient.mutation.updateItem(
			{
				itemId: createdOperation.id,
				boardId: context.boards.briefs,
				columnValues,
			},
			context,
		);

		if (thumbnail) {
			await mondayClient.mutation.uploadFile({
				file: thumbnail!,
				itemId: createdOperation.id,
				columnId: OPERATION_COLUMNS.thumbnail,
			});
		}

		await Promise.all(
			files.filter(Boolean).map((file) =>
				mondayClient.mutation.uploadFile({
					file: file!,
					itemId: createdOperation.id,
					columnId: OPERATION_COLUMNS.files,
				}),
			),
		);

		await createOperationSubItems(createdOperation.id, data.subItems);
		return createdOperation;
	});

export const createOperationOnServer = wrapWithUseMutation<
	typeof handler,
	OperationMutationVariables
>(handler, {
	mutationFn: async ({ files, thumbnail, ...params }) => {
		const formData = new FormData();

		for (const file of files || []) {
			formData.append("files", file, file.name);
		}
		if (thumbnail) formData.append("thumbnail", thumbnail, thumbnail.name);

		formData.append("data", JSON.stringify(params));

		const rep = await handler({ data: formData });

		return rep;
	},
});
